import {
  arrayUnion,
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDocs,
  setDoc,
  getDoc,
  serverTimestamp,
  Timestamp,
  limit,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';

import { getFirestoreDb } from '../config/firebaseApp';
import type { BabyGender, UserProfile, UserRole } from '../types/models';
import { getBabyAgeInMonths } from '../utils/babyAge';

const USERS = 'users';

/** Soft delete sonrası giriş yapılmazsa kalıcı silme süresi (gün). */
export const ACCOUNT_DELETION_GRACE_DAYS = 30;

export function isAccountPendingDeletion(p: UserProfile | null | undefined): boolean {
  return p?.accountStatus === 'pending_deletion' && Boolean(p.deletionRequestedAt);
}

export function isDeletionGraceExpired(deletionRequestedAt: Timestamp): boolean {
  const deadline =
    deletionRequestedAt.toMillis() + ACCOUNT_DELETION_GRACE_DAYS * 24 * 60 * 60 * 1000;
  return Date.now() > deadline;
}

/** Üyelik askıya alınır (Firestore); oturum ayrıca kapatılmalı. */
export async function requestAccountDeletion(uid: string): Promise<void> {
  await updateDoc(doc(getFirestoreDb(), USERS, uid), {
    accountStatus: 'pending_deletion',
    deletionRequestedAt: serverTimestamp(),
  });
}

/** Askıdaki hesabı tekrar normale döndürür. */
export async function cancelAccountDeletion(uid: string): Promise<void> {
  await updateDoc(doc(getFirestoreDb(), USERS, uid), {
    accountStatus: 'active',
    deletionRequestedAt: deleteField(),
  });
}

/** Kalıcı silme: Firestore kullanıcı belgesi (Auth ayrı silinir). */
export async function deleteUserDocument(uid: string): Promise<void> {
  await deleteDoc(doc(getFirestoreDb(), USERS, uid));
}

export async function saveUserProfile(
  uid: string,
  data: {
    role: UserRole;
    displayName: string;
    parentAge: number;
    babyBirthDate: Date;
    babyGender: BabyGender;
    city: string;
    district: string;
  },
): Promise<void> {
  const babyBirthDate = Timestamp.fromDate(
    new Date(data.babyBirthDate.getFullYear(), data.babyBirthDate.getMonth(), 1),
  );
  await setDoc(
    doc(getFirestoreDb(), USERS, uid),
    {
      id: uid,
      role: data.role,
      displayName: data.displayName.trim(),
      parentAge: data.parentAge,
      babyBirthDate,
      babyGender: data.babyGender,
      city: data.city.trim(),
      district: data.district.trim(),
      createdAt: serverTimestamp(),
    },
    { merge: true },
  );
}

/** Profil düzenleme — `createdAt` dokunulmaz (merge). */
export async function updateUserProfile(
  uid: string,
  data: {
    role: UserRole;
    displayName: string;
    parentAge: number;
    babyBirthDate: Date;
    babyGender: BabyGender;
    city: string;
    district: string;
  },
): Promise<void> {
  const babyBirthDate = Timestamp.fromDate(
    new Date(data.babyBirthDate.getFullYear(), data.babyBirthDate.getMonth(), 1),
  );
  await setDoc(
    doc(getFirestoreDb(), USERS, uid),
    {
      id: uid,
      role: data.role,
      displayName: data.displayName.trim(),
      parentAge: data.parentAge,
      babyBirthDate,
      babyGender: data.babyGender,
      city: data.city.trim(),
      district: data.district.trim(),
    },
    { merge: true },
  );
}

export async function getUserProfile(uid: string): Promise<UserProfile | null> {
  const snap = await getDoc(doc(getFirestoreDb(), USERS, uid));
  if (!snap.exists()) {
    return null;
  }
  return snap.data() as UserProfile;
}

/** Profildeki `blockedUsers` için istemci tarafı filtreleme kümesi. */
export function getBlockedUserIdsSet(profile: UserProfile | null | undefined): Set<string> {
  const raw = profile?.blockedUsers;
  if (!Array.isArray(raw)) {
    return new Set();
  }
  return new Set(raw.filter((id): id is string => typeof id === 'string' && id.length > 0));
}

/** Engellenen kullanıcı UID’sini kendi `users/{uid}` belgesine ekler. */
export async function blockUser(currentUid: string, blockedUid: string): Promise<void> {
  const b = blockedUid.trim();
  if (!b || b === currentUid) {
    return;
  }
  await updateDoc(doc(getFirestoreDb(), USERS, currentUid), {
    blockedUsers: arrayUnion(b),
  });
}

/** Yeni sohbet seçici: tüm kullanıcılar (giriş yapan hariç), ada göre sıralı. */
export async function listUsersExcept(excludeUid: string): Promise<UserProfile[]> {
  const snap = await getDocs(collection(getFirestoreDb(), USERS));
  const list: UserProfile[] = [];
  snap.forEach(d => {
    if (d.id === excludeUid) {
      return;
    }
    const data = d.data() as Omit<UserProfile, 'id'> & { id?: string };
    if (data.accountStatus === 'pending_deletion') {
      return;
    }
    list.push({ ...data, id: d.id });
  });
  list.sort((a, b) =>
    (a.displayName || '').localeCompare(b.displayName || '', 'tr', { sensitivity: 'base' }),
  );
  return list;
}

export function isProfileComplete(p: UserProfile | null): boolean {
  if (!p) {
    return false;
  }
  return Boolean(
    p.displayName && p.role && p.city && p.district && p.babyBirthDate,
  );
}

const PLAYDATE_AGE_MARGIN_MONTHS = 3;
const PLAYDATE_QUERY_LIMIT = 120;

/**
 * Aynı il + ilçe; `id !=` Firestore sorgusu. Bebek yaşı ±3 ay eşleşmesi istemcide filtrelenir.
 * (Gerekirse `firebase deploy` ile `firestore.indexes.json` indeksini yayınlayın.)
 */
export async function getPlaydateMatches(
  currentUserProfile: UserProfile,
): Promise<UserProfile[]> {
  const city = currentUserProfile.city?.trim();
  const district = currentUserProfile.district?.trim();
  const selfId = currentUserProfile.id?.trim();
  if (!city || !district || !selfId || !currentUserProfile.babyBirthDate) {
    return [];
  }

  const myAgeMonths = getBabyAgeInMonths(currentUserProfile.babyBirthDate);
  const db = getFirestoreDb();
  const q = query(
    collection(db, USERS),
    where('city', '==', city),
    where('district', '==', district),
    where('id', '!=', selfId),
    limit(PLAYDATE_QUERY_LIMIT),
  );
  const snap = await getDocs(q);
  const out: UserProfile[] = [];
  snap.forEach(d => {
    if (d.id === selfId) {
      return;
    }
    const raw = d.data() as UserProfile;
    const u: UserProfile = { ...raw, id: d.id };
    if (u.accountStatus === 'pending_deletion') {
      return;
    }
    if (!u.babyBirthDate) {
      return;
    }
    const theirAge = getBabyAgeInMonths(u.babyBirthDate);
    if (Math.abs(theirAge - myAgeMonths) > PLAYDATE_AGE_MARGIN_MONTHS) {
      return;
    }
    out.push(u);
  });
  out.sort((a, b) =>
    (a.displayName || '').localeCompare(b.displayName || '', 'tr', { sensitivity: 'base' }),
  );
  return out;
}
